import React, { useState, useEffect } from "react";
import { AVATARS } from "../../config/data";
import { controlBgm } from "../../utils/soundUtils";
import { auth } from "../../config/firebase";
import { GoogleAuthProvider, signInWithPopup, signOut, onAuthStateChanged } from "firebase/auth";

// รับ prop 'kickedMessage' เพิ่ม
export default function WelcomeScreen({
  onStartGame,
  onOpenSettings,
  soundSettings,
  bgmAudioRef,
  initialRoomId,
  kickedMessage,
}) {
  const [userName, setUserName] = useState(
    () => localStorage.getItem("murderMystery_username") || ""
  );
  const [roomId, setRoomId] = useState("");
  const [selectedAvatarIndex, setSelectedAvatarIndex] = useState(() => {
    const saved = localStorage.getItem("murderMystery_avatarIndex");
    const index = parseInt(saved, 10);
    return !isNaN(index) && index >= 0 && index < AVATARS.length ? index : 0;
  });

  useEffect(() => {
    localStorage.setItem("murderMystery_avatarIndex", selectedAvatarIndex);
  }, [selectedAvatarIndex]);

  // Preload Images
  useEffect(() => {
    AVATARS.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  const [animationClass, setAnimationClass] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [mode, setMode] = useState(initialRoomId ? "join" : "create");

  const [googleUser, setGoogleUser] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user && !user.isAnonymous) {
        setGoogleUser(user);
        setUserName(user.displayName || "");
      } else {
        setGoogleUser(null);
      }
    });
    return () => unsubscribe();
  }, []);

  const handleGoogleSignIn = async () => {
    const provider = new GoogleAuthProvider();
    try {
      setError("");
      setIsLoading(true);
      await signInWithPopup(auth, provider);
    } catch (err) {
      console.error("Google Sign-In Error:", err);
      setError("การล็อกอินด้วย Google ล้มเหลว: " + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignOut = async () => {
    try {
      setError("");
      setIsLoading(true);
      await signOut(auth);
      setUserName(localStorage.getItem("murderMystery_username") || "");
    } catch (err) {
      console.error("Google Sign-Out Error:", err);
      setError("การออกจากระบบล้มเหลว: " + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (initialRoomId) {
      setRoomId(initialRoomId);
      setMode("join");
    }
  }, [initialRoomId]);

  // ถ้ามี kickedMessage ให้สลับมาหน้า join เพื่อให้เห็นข้อความ
  useEffect(() => {
    if (kickedMessage) {
      setMode("join");
    }
  }, [kickedMessage]);

  useEffect(() => {
    controlBgm(
      bgmAudioRef,
      "/sounds/bgm_menu.mp3",
      soundSettings.isBgmMuted
    );
  }, [soundSettings.isBgmMuted, bgmAudioRef]);

  const handlePrevAvatar = () => {
    if (animationClass) return;
    setAnimationClass("animate-slide-out-right");
    setTimeout(() => {
      const newIndex =
        (selectedAvatarIndex - 1 + AVATARS.length) % AVATARS.length;
      setSelectedAvatarIndex(newIndex);
      setAnimationClass("animate-slide-in-from-left");
      setTimeout(() => setAnimationClass(""), 200);
    }, 150);
  };

  const handleNextAvatar = () => {
    if (animationClass) return;
    setAnimationClass("animate-slide-out-left");
    setTimeout(() => {
      const newIndex = (selectedAvatarIndex + 1) % AVATARS.length;
      setSelectedAvatarIndex(newIndex);
      setAnimationClass("animate-slide-in-from-right");
      setTimeout(() => setAnimationClass(""), 200);
    }, 150);
  };

  const saveUserName = () => {
    if (userName.trim()) {
      localStorage.setItem("murderMystery_username", userName.trim());
    }
  };

  const handleCreateRoom = async (e) => {
    if (e) e.preventDefault();

    if (!userName.trim()) {
      setError("กรุณาใส่ชื่อผู้เล่น");
      return;
    }

    saveUserName();

    setIsLoading(true);
    const success = await onStartGame({
      userName: userName.trim(),
      avatar: googleUser ? googleUser.photoURL : AVATARS[selectedAvatarIndex],
      isGoogle: !!googleUser,
      roomId: null,
      isCreator: true,
    });

    if (!success) {
      setIsLoading(false);
    }
  };

  const handleJoinRoom = async (e) => {
    if (e) e.preventDefault();

    if (!userName.trim()) {
      setError("กรุณาใส่ชื่อผู้เล่น");
      return;
    }
    if (!roomId.trim()) {
      setError("กรุณาใส่ ID ห้อง");
      return;
    }

    saveUserName();

    setIsLoading(true);
    const success = await onStartGame({
      userName: userName.trim(),
      avatar: googleUser ? googleUser.photoURL : AVATARS[selectedAvatarIndex],
      isGoogle: !!googleUser,
      roomId: roomId.trim(),
      isCreator: false,
    });

    if (!success) {
      setIsLoading(false);
      setError("ไม่สามารถเข้าร่วมห้องได้");
    }
  };

  const handleInputFocus = (e) => {
    if (window.innerWidth < 768) {
      setTimeout(() => {
        e.target.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 300);
    }
  };

  return (
    <>
      <button
        onClick={onOpenSettings}
        className="fixed top-4 right-4 z-40 bg-gray-800/70 hover:bg-gray-700 text-white p-3 rounded-full shadow-lg transition-transform hover:scale-110 active:scale-95"
        title="ตั้งค่าเกม"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.096 2.572-1.065z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
      </button>

      <div
        className="min-h-screen bg-gray-900 text-white flex flex-col items-center justify-center p-4 font-sans bg-cover bg-center"
        style={{ backgroundImage: `url("/images/desk_background.jpg")` }}
      >
        <div className="w-full max-w-sm mx-auto">
          <img
            src="/logo.png"
            alt="Game Logo"
            className="w-full max-w-lg h-auto mx-auto mb-4 drop-shadow-[0_5px_15px_rgba(0,0,0,0.5)]"
          />

          <div className="bg-[#f3e9d2] p-6 rounded-lg shadow-2xl text-gray-800 border-4 border-[#c8bda9]">
            <h2 className="text-2xl font-bold text-center mb-4 font-serif text-[#5a4a3a]">
              สร้างโปรไฟล์ของคุณ
            </h2>

            <div className="flex flex-col items-center mb-4">
              {googleUser ? (
                <div className="flex flex-col items-center gap-2">
                  <div className="w-32 h-32 grid place-items-center rounded-full bg-[#e1d4be] ring-2 ring-[#a08d70] overflow-hidden shadow-md">
                    <img
                      src={googleUser.photoURL}
                      alt="Google Profile"
                      className="w-full h-full object-cover rounded-full"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleGoogleSignOut}
                    disabled={isLoading}
                    className="text-xs text-red-600 hover:text-red-800 underline font-semibold mt-1"
                  >
                    ออกจากระบบ Google
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-around gap-4 w-full">
                  <button
                    type="button"
                    onClick={handlePrevAvatar}
                    className="h-16 w-16 rounded-full bg-[#c8bda9] hover:bg-[#b0a08e] transition text-[#5a4a3a] flex-shrink-0 shadow-md transform hover:scale-110 flex items-center justify-center active:scale-95 active:shadow-inner"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-8 w-8"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={3}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </button>

                  <div className="w-32 h-32 grid place-items-center rounded-full bg-[#e1d4be] ring-2 ring-[#a08d70] overflow-hidden shadow-inner">
                    <img
                      src={AVATARS[selectedAvatarIndex]}
                      alt="Avatar"
                      className={`w-full h-full object-contain ${animationClass}`}
                    />
                  </div>

                  <button
                    type="button"
                    onClick={handleNextAvatar}
                    className="h-16 w-16 rounded-full bg-[#c8bda9] hover:bg-[#b0a08e] transition text-[#5a4a3a] flex-shrink-0 shadow-md transform hover:scale-110 flex items-center justify-center active:scale-95 active:shadow-inner"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-8 w-8"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={3}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                </div>
              )}
            </div>

            {!googleUser && (
              <button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={isLoading}
                className="w-full mb-4 bg-white hover:bg-gray-100 text-gray-700 font-bold py-2.5 px-4 rounded-lg shadow border border-gray-300 flex items-center justify-center gap-2 transition-all duration-200 active:scale-95"
              >
                <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v3.92h6.69c-.29 1.5-1.14 2.77-2.4 3.63v3.02h3.89c2.28-2.1 3.56-5.18 3.56-8.8z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 24c3.24 0 5.97-1.08 7.96-2.91l-3.89-3.02c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.11C3.26 20.19 7.35 24 12 24z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.27 14.27c-.25-.72-.39-1.5-.39-2.31 0-.81.14-1.59.39-2.31V6.53H1.29C.47 8.17 0 10 0 12s.47 3.83 1.29 5.47l3.98-3.2z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.44-3.44C17.97 1.19 15.24 0 12 0 7.35 0 3.26 3.81 1.29 7.47l3.98 3.2c.95-2.85 3.6-4.96 6.73-4.96z"
                  />
                </svg>
                ล็อกอินด้วย Google
              </button>
            )}

            <input
              type="text"
              placeholder="ชื่อของคุณ"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              onFocus={handleInputFocus}
              className="w-full p-3 mb-4 bg-white/80 rounded border-2 border-[#c8bda9] focus:outline-none focus:ring-2 focus:ring-[#8b0000] text-center text-gray-900 placeholder-gray-500"
            />

            <div className="space-y-4">
              <div className="flex bg-[#e1d4be] p-1 rounded-lg">
                <button
                  onClick={() => setMode("create")}
                  className={`flex-1 py-2 rounded-md font-bold text-sm transition-all duration-300 ${mode === "create"
                    ? "bg-[#8b6b4e] text-white shadow-md"
                    : "text-[#5a4a3a] hover:bg-[#d4c5ab]"
                    }`}
                >
                  สร้างห้องใหม่
                </button>
                <button
                  onClick={() => setMode("join")}
                  className={`flex-1 py-2 rounded-md font-bold text-sm transition-all duration-300 ${mode === "join"
                    ? "bg-[#8b6b4e] text-white shadow-md"
                    : "text-[#5a4a3a] hover:bg-[#d4c5ab]"
                    }`}
                >
                  เข้าร่วมห้อง
                </button>
              </div>

              <div className="relative overflow-hidden w-full">
                <div
                  className={`flex w-[200%] transition-transform duration-300 ease-in-out ${mode === "join" ? "-translate-x-1/2" : "translate-x-0"
                    }`}
                >
                  <div className="w-1/2 py-2 px-2">
                    <button
                      onClick={handleCreateRoom}
                      disabled={isLoading}
                      className="w-full bg-[#556b2f] hover:bg-[#6B8E23] text-white font-bold py-3 px-4 rounded-lg shadow-md transform hover:scale-105 transition-transform border border-[#5a4a3a] active:scale-95 border-b-4 border-black/20 active:shadow-inner active:translate-y-px active:border-b-2"
                    >
                      สร้างห้องใหม่
                    </button>
                  </div>

                  <div className="w-1/2 py-2 px-2">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="ใส่ ID ห้อง"
                        value={roomId}
                        onChange={(e) => setRoomId(e.target.value)}
                        onFocus={handleInputFocus}
                        className="flex-grow min-w-0 p-3 bg-[#e1d4be] rounded border-2 border-[#c8bda9] focus:outline-none focus:ring-2 focus:ring-[#8b6b4e] text-gray-900 placeholder-gray-600 font-bold tracking-widest text-center"
                      />
                      <button
                        onClick={handleJoinRoom}
                        disabled={isLoading}
                        className="bg-[#a08d70] hover:bg-[#8d7c62] text-white font-bold py-2 px-2 sm:px-4 rounded-lg shadow-md transform hover:scale-105 transition-transform border border-[#5a4a3a] active:scale-95 border-b-4 border-black/20 active:shadow-inner active:translate-y-px active:border-b-2 whitespace-nowrap text-sm sm:text-base"
                      >
                        เข้าร่วม
                      </button>
                    </div>
                    {/* แสดงข้อความแจ้งเตือนเมื่อโดนเตะ (เฉพาะในหน้า Join) */}
                    {kickedMessage && (
                      <p className="text-red-600 text-xs sm:text-sm font-bold text-center mt-2 animate-pulse bg-white/50 p-1 rounded">
                        {kickedMessage}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Error ปกติ (เช่น ใส่ชื่อไม่ครบ) */}
            {error && !kickedMessage && (
              <p className="text-red-600 mt-4 text-center font-semibold animate-pulse">
                {error}
              </p>
            )}
          </div>

          <p
            className="mt-4 text-xs text-center text-gray-300 font-mono tracking-wide opacity-80"
            style={{ textShadow: "1px 1px 2px black" }}
          >
            สร้างโดย Google Gemini
            <svg
              viewBox="0 0 24 24"
              className="inline-block w-4 h-4 mx-1"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <defs>
                <linearGradient
                  id="geminiBluePurple"
                  x1="0%"
                  y1="50%"
                  x2="100%"
                  y2="50%"
                >
                  <stop offset="0%" stopColor="#7C3AED" />
                  <stop offset="100%" stopColor="#2563EB" />
                </linearGradient>
              </defs>
              <path
                d="M12 2L14.2 9.6C14.5 10.7 15.3 11.5 16.4 11.8L24 14L16.4 16.2C15.3 16.5 14.5 17.3 14.2 18.4L12 26L9.8 18.4C9.5 17.3 8.7 16.5 7.6 16.2L0 14L7.6 11.8C8.7 11.5 9.5 10.7 9.8 9.6L12 2Z"
                fill="url(#geminiBluePurple)"
                transform="scale(0.8) translate(6, 0)"
              />
              <path
                d="M18 4L18.9 7.1C19 7.5 19.3 7.8 19.7 7.9L22.8 8.8L19.7 9.7C19.3 9.8 19 10.1 18.9 10.5L18 13.6L17.1 10.5C17 10.1 16.7 9.8 16.3 9.7L13.2 8.8L16.3 7.9C16.7 7.8 17 7.5 17.1 7.1L18 4Z"
                fill="url(#geminiBluePurple)"
              />
            </svg>
            <br />@ Lonely Dog Studio (2025)
          </p>
          <p
            className="mt-2 text-[10px] text-center text-gray-400 font-sans tracking-wide opacity-75 max-w-[280px] sm:max-w-xs mx-auto leading-relaxed"
            style={{ textShadow: "1px 1px 2px black" }}
          >
            แอปพลิเคชันนี้จัดทำขึ้นเพื่อวัตถุประสงค์ทางการศึกษา Vibe Coding เท่านั้น โดยไม่มีวัตถุประสงค์ทางการค้าใด ๆ ทั้งสิ้น
          </p>
        </div>
      </div>
    </>
  );
}
